import React, { useState, useEffect } from 'react'
import { apiService } from '../services/api'
import { 
  UserGroupIcon, 
  ClockIcon, 
  CalendarIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

interface Employee {
  id: string
  employee_code: string
  first_name: string
  last_name: string
  email: string
  phone: string
  designation: string
  department: string
  hire_date: string
  status: string
}

interface Attendance {
  id: string
  employee_id: string
  attendance_date: string
  check_in: string
  check_out: string
  total_hours: number
  status: string
}

interface LeaveRequest {
  id: string
  employee_id: string
  leave_type_id: string
  start_date: string
  end_date: string
  reason: string
  status: string
}

interface Payroll {
  id: string
  employee_id: string
  pay_period_start: string
  pay_period_end: string
  basic_salary: number
  allowances: number
  deductions: number
  net_salary: number
  status: string
}

interface Loan {
  id: string
  loan_number: string
  employee_id: string
  loan_type_id: string
  loan_amount: number
  loan_date: string
  installment_amount: number
  total_installments: number
  paid_installments: number
  remaining_balance: number
  status: string
  employees?: any
  loan_types?: any
}

const HRManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('employees')
  const [employees, setEmployees] = useState<Employee[]>([])
  const [attendance, setAttendance] = useState<Attendance[]>([])
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([])
  const [leaveTypes, setLeaveTypes] = useState<any[]>([])
  const [loanTypes, setLoanTypes] = useState<any[]>([])
  const [loans, setLoans] = useState<Loan[]>([])
  const [payroll, setPayroll] = useState<Payroll[]>([])
  const [loading, setLoading] = useState(false)

  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showProcessModal, setShowProcessModal] = useState(false)
  const [showDeleteEmployeeModal, setShowDeleteEmployeeModal] = useState(false)
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null)
  const [showDeleteAttendanceModal, setShowDeleteAttendanceModal] = useState(false)
  const [attendanceToDelete, setAttendanceToDelete] = useState<Attendance | null>(null)
  const [showDeleteLeaveModal, setShowDeleteLeaveModal] = useState(false)
  const [leaveToDelete, setLeaveToDelete] = useState<LeaveRequest | null>(null)
  const [showApproveLeaveModal, setShowApproveLeaveModal] = useState(false)
  const [leaveToApprove, setLeaveToApprove] = useState<LeaveRequest | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [showDeletePayrollModal, setShowDeletePayrollModal] = useState(false)
  const [payrollToDelete, setPayrollToDelete] = useState<Payroll | null>(null)
  const [showDeleteLoanModal, setShowDeleteLoanModal] = useState(false)
  const [loanToDelete, setLoanToDelete] = useState<Loan | null>(null)
  const [modalType, setModalType] = useState<'add' | 'edit'>('add')
  const [editingItem, setEditingItem] = useState<any>(null)
  const [viewingPayroll, setViewingPayroll] = useState<any>(null)
  const [selectedPayrolls, setSelectedPayrolls] = useState<string[]>([])
  const [processingPayrolls, setProcessingPayrolls] = useState<any[]>([])

  // Form states
  const [employeeForm, setEmployeeForm] = useState({
    employee_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    designation: '',
    department: '',
    base_salary: '',
    allowance: '',
    bonus: '',
    working_days_per_week: 6,
    weekend_days: ['Friday', 'Saturday'],
    daily_working_hours: 8,
    status: 'active'
  })

  const [attendanceForm, setAttendanceForm] = useState({
    employee_id: '',
    attendance_date: new Date().toISOString().split('T')[0],
    check_in: '',
    check_out: '',
    status: 'present'
  })

  const [leaveForm, setLeaveForm] = useState({
    employee_id: '',
    leave_type_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    reason: '',
    status: 'pending'
  })

  const [payrollForm, setPayrollForm] = useState({
    employee_id: '',
    pay_period_start: '',
    pay_period_end: ''
  })

  const [loanForm, setLoanForm] = useState({
    employee_id: '',
    loan_type_id: '',
    loan_amount: '',
    loan_date: new Date().toISOString().split('T')[0],
    installment_amount: '',
    total_installments: '',
    description: ''
  })

  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  useEffect(() => {
    loadData()
    loadLoanTypes()
  }, [activeTab])

  useEffect(() => {
    // Load leave types when component mounts
    loadLeaveTypes()
    loadLoanTypes()
  }, [])

  const loadLeaveTypes = async () => {
    try {
      const leaveTypesRes = await apiService.getLeaveTypes()
      if (leaveTypesRes.success && leaveTypesRes.data && Array.isArray(leaveTypesRes.data)) {
        setLeaveTypes(leaveTypesRes.data as any[])
      }
    } catch (error) {
      console.error('Error loading leave types:', error)
    }
  }

  const loadLoanTypes = async () => {
    try {
      const loanTypesRes = await apiService.getLoanTypes()
      if (loanTypesRes.success && loanTypesRes.data && Array.isArray(loanTypesRes.data)) {
        setLoanTypes(loanTypesRes.data as any[])
      }
    } catch (error) {
      console.error('Error loading loan types:', error)
    }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      switch (activeTab) {
        case 'employees':
          const employeesRes = await apiService.getEmployees()
          if (employeesRes.success && employeesRes.data && Array.isArray(employeesRes.data)) {
            setEmployees(employeesRes.data as Employee[])
          }
          break
        case 'attendance':
          const attendanceRes = await apiService.getAttendance()
          if (attendanceRes.success && attendanceRes.data && Array.isArray(attendanceRes.data)) {
            setAttendance(attendanceRes.data as Attendance[])
          }
          break
        case 'leaves':
          const leavesRes = await apiService.getLeaveRequests()
          if (leavesRes.success && leavesRes.data && Array.isArray(leavesRes.data)) {
            setLeaveRequests(leavesRes.data as LeaveRequest[])
          }
          break
        case 'payroll':
          const payrollRes = await apiService.getPayrollList()
          if (payrollRes.success && payrollRes.data && Array.isArray(payrollRes.data)) {
            setPayroll(payrollRes.data as Payroll[])
            // Recalculate payroll records that don't have working days calculated
            const recordsNeedingRecalc = (payrollRes.data as Payroll[]).filter(
              (record: any) => !record.total_working_days || record.total_working_days === 0
            )
            if (recordsNeedingRecalc.length > 0) {
              // Auto-recalculate all records
              try {
                await apiService.recalculateAllPayroll()
                // Reload payroll data after recalculation
                const updatedPayrollRes = await apiService.getPayrollList()
                if (updatedPayrollRes.success && updatedPayrollRes.data && Array.isArray(updatedPayrollRes.data)) {
                  setPayroll(updatedPayrollRes.data as Payroll[])
                }
              } catch (error) {
                console.error('Error recalculating payroll:', error)
              }
            }
          }
          break
        case 'loans':
          const loansRes = await apiService.getLoans()
          if (loansRes.success && loansRes.data && Array.isArray(loansRes.data)) {
            setLoans(loansRes.data as Loan[])
          }
          break
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number | string | null | undefined) => {
    const n = typeof amount === 'number' && !Number.isNaN(amount) ? amount : Number(amount) || 0
    return `SAR ${n.toFixed(2)}`
  }

  const showToastMessage = (message: string, type: 'success' | 'error') => {
    setToastMessage(message)
    setToastType(type)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)
  }

  // Employee handlers
  const handleAddEmployee = () => {
    setModalType('add')
    setEditingItem(null)
    setEmployeeForm({
      employee_code: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      designation: '',
      department: '',
      base_salary: '',
      allowance: '',
      bonus: '',
      working_days_per_week: 6,
      weekend_days: ['Friday', 'Saturday'],
      daily_working_hours: 8,
      status: 'active'
    })
    setShowModal(true)
  }

  const handleEditEmployee = (employee: Employee) => {
    setModalType('edit')
    setEditingItem(employee)
    setEmployeeForm({
      employee_code: employee.employee_code || '',
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      designation: employee.designation || '',
      department: employee.department || '',
      base_salary: (employee as any).base_salary?.toString() || '',
      allowance: (employee as any).allowance?.toString() || '',
      bonus: (employee as any).bonus?.toString() || '',
      working_days_per_week: (employee as any).working_days_per_week || 6,
      weekend_days: (employee as any).weekend_days || ['Friday', 'Saturday'],
      daily_working_hours: (employee as any).daily_working_hours || 8,
      status: employee.status || 'active'
    })
    setShowModal(true)
  }

  const handleSaveEmployee = async () => {
    try {
      if (!employeeForm.first_name || !employeeForm.email || !employeeForm.base_salary) {
        showToastMessage('Please fill in all required fields (First Name, Email, and Base Salary)', 'error')
        return
      }

      // Prepare employee data with salary fields and working schedule
      const employeeData = {
        ...employeeForm,
        base_salary: parseFloat(employeeForm.base_salary) || 0,
        allowance: parseFloat(employeeForm.allowance) || 0,
        bonus: parseFloat(employeeForm.bonus) || 0,
        working_days_per_week: parseInt(employeeForm.working_days_per_week.toString()) || 6,
        weekend_days: Array.isArray(employeeForm.weekend_days) ? employeeForm.weekend_days : (employeeForm.weekend_days ? JSON.parse(employeeForm.weekend_days) : ['Friday', 'Saturday']),
        daily_working_hours: parseFloat(employeeForm.daily_working_hours.toString()) || 8
      }

      let response
      if (modalType === 'add') {
        response = await apiService.createEmployee(employeeData)
      } else {
        response = await apiService.updateEmployee(editingItem.id, employeeData)
      }

      if (response.success) {
        showToastMessage(
          modalType === 'add' ? 'Employee created successfully' : 'Employee updated successfully',
          'success'
        )
        setShowModal(false)
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to save employee', 'error')
      }
    } catch (error) {
      console.error('Error saving employee:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const handleDeleteEmployee = (employee: Employee) => {
    setEmployeeToDelete(employee)
    setShowDeleteEmployeeModal(true)
  }

  const confirmDeleteEmployee = async () => {
    if (!employeeToDelete) return

    try {
      const response = await apiService.deleteEmployee(employeeToDelete.id)
      if (response.success) {
        showToastMessage('Employee deleted successfully', 'success')
        loadData()
        setShowDeleteEmployeeModal(false)
        setEmployeeToDelete(null)
      } else {
        showToastMessage(response.error || 'Failed to delete employee', 'error')
      }
    } catch (error) {
      console.error('Error deleting employee:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const handleDeleteAttendance = (attendance: Attendance) => {
    setAttendanceToDelete(attendance)
    setShowDeleteAttendanceModal(true)
  }

  const confirmDeleteAttendance = async () => {
    if (!attendanceToDelete) return

    try {
      const response = await apiService.deleteAttendance(attendanceToDelete.id)
      if (response.success) {
        showToastMessage('Attendance record deleted successfully', 'success')
        loadData()
        setShowDeleteAttendanceModal(false)
        setAttendanceToDelete(null)
      } else {
        showToastMessage(response.error || 'Failed to delete attendance record', 'error')
      }
    } catch (error) {
      console.error('Error deleting attendance:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const handleDeleteLeaveRequest = (leaveRequest: LeaveRequest) => {
    setLeaveToDelete(leaveRequest)
    setShowDeleteLeaveModal(true)
  }

  const confirmDeleteLeaveRequest = async () => {
    if (!leaveToDelete) return

    try {
      const response = await apiService.deleteLeaveRequest(leaveToDelete.id)
      if (response.success) {
        showToastMessage('Leave request deleted successfully', 'success')
        loadData()
        setShowDeleteLeaveModal(false)
        setLeaveToDelete(null)
      } else {
        showToastMessage(response.error || 'Failed to delete leave request', 'error')
      }
    } catch (error) {
      console.error('Error deleting leave request:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const handleApproveLeaveRequest = (request: LeaveRequest) => {
    setLeaveToApprove(request)
    setRejectionReason('')
    setShowApproveLeaveModal(true)
  }

  const confirmApproveLeaveRequest = async () => {
    if (!leaveToApprove) return

    try {
      const response = await apiService.approveLeaveRequest(leaveToApprove.id)
      if (response.success) {
        showToastMessage('Leave request approved successfully', 'success')
        loadData()
        setShowApproveLeaveModal(false)
        setLeaveToApprove(null)
        setRejectionReason('')
      } else {
        showToastMessage(response.error || 'Failed to approve leave request', 'error')
      }
    } catch (error) {
      console.error('Error approving leave request:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const confirmRejectLeaveRequest = async () => {
    if (!leaveToApprove) return

    try {
      const response = await apiService.rejectLeaveRequest(leaveToApprove.id, undefined, rejectionReason)
      if (response.success) {
        showToastMessage('Leave request rejected successfully', 'success')
        loadData()
        setShowApproveLeaveModal(false)
        setLeaveToApprove(null)
        setRejectionReason('')
      } else {
        showToastMessage(response.error || 'Failed to reject leave request', 'error')
      }
    } catch (error) {
      console.error('Error rejecting leave request:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const handleDeletePayroll = (payroll: Payroll) => {
    setPayrollToDelete(payroll)
    setShowDeletePayrollModal(true)
  }

  const confirmDeletePayroll = async () => {
    if (!payrollToDelete) return

    try {
      const response = await apiService.deletePayroll(payrollToDelete.id)
      if (response.success) {
        showToastMessage('Payroll record deleted successfully', 'success')
        loadData()
        setShowDeletePayrollModal(false)
        setPayrollToDelete(null)
      } else {
        showToastMessage(response.error || 'Failed to delete payroll record', 'error')
      }
    } catch (error) {
      console.error('Error deleting payroll:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  // Attendance handlers
  const handleAddAttendance = () => {
    setModalType('add')
    setEditingItem(null)
    setAttendanceForm({
      employee_id: employees.length > 0 ? employees[0].id : '',
      attendance_date: new Date().toISOString().split('T')[0],
      check_in: '',
      check_out: '',
      status: 'present'
    })
    setShowModal(true)
  }

  const handleSaveAttendance = async () => {
    try {
      if (!attendanceForm.employee_id || !attendanceForm.attendance_date || !attendanceForm.check_in) {
        showToastMessage('Please fill in all required fields', 'error')
        return
      }

      const data = {
        employee_id: attendanceForm.employee_id,
        attendance_date: attendanceForm.attendance_date,
        check_in: attendanceForm.check_in,
        check_out: attendanceForm.check_out || null,
        status: attendanceForm.status
      }

      const response = await apiService.createAttendance(data)
      if (response.success) {
        showToastMessage('Attendance record created successfully', 'success')
        setShowModal(false)
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to save attendance record', 'error')
      }
    } catch (error) {
      console.error('Error saving attendance:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  // Leave Request handlers
  const handleAddLeaveRequest = () => {
    setModalType('add')
    setEditingItem(null)
    setLeaveForm({
      employee_id: employees.length > 0 ? employees[0].id : '',
      leave_type_id: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      reason: '',
      status: 'pending'
    })
    setShowModal(true)
  }

  const handleSaveLeaveRequest = async () => {
    try {
      if (!leaveForm.employee_id || !leaveForm.start_date || !leaveForm.end_date || !leaveForm.leave_type_id) {
        showToastMessage('Please fill in all required fields', 'error')
        return
      }

      // Calculate total days
      const startDate = new Date(leaveForm.start_date)
      const endDate = new Date(leaveForm.end_date)
      const timeDiff = endDate.getTime() - startDate.getTime()
      const totalDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + 1 // +1 to include both start and end dates

      const data = {
        employee_id: leaveForm.employee_id,
        leave_type_id: leaveForm.leave_type_id,
        start_date: leaveForm.start_date,
        end_date: leaveForm.end_date,
        total_days: totalDays,
        reason: leaveForm.reason || null,
        status: leaveForm.status
      }

      let response
      if (modalType === 'add') {
        response = await apiService.createLeaveRequest(data)
      } else {
        response = await apiService.updateLeaveRequest(editingItem.id, data)
      }

      if (response.success) {
        showToastMessage(
          modalType === 'add' ? 'Leave request created successfully' : 'Leave request updated successfully',
          'success'
        )
        setShowModal(false)
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to save leave request', 'error')
      }
    } catch (error) {
      console.error('Error saving leave request:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const handleViewLeaveRequest = (request: any) => {
    setEditingItem(request)
    setModalType('edit')
    setLeaveForm({
      employee_id: request.employee_id,
      leave_type_id: request.leave_type_id,
      start_date: request.start_date,
      end_date: request.end_date,
      reason: request.reason || '',
      status: request.status
    })
    setShowModal(true)
  }

  const handleViewPayroll = (payroll: any) => {
    setViewingPayroll(payroll)
    setShowViewModal(true)
  }

  const handleProcessPayrollRecord = (payrollData?: any) => {
    if (payrollData) {
      // Single or bulk payroll processing - payrollData is an array
      const payrollsToProcess = Array.isArray(payrollData) ? payrollData : [payrollData]
      setProcessingPayrolls(payrollsToProcess)
      setShowProcessModal(true)
    } else {
      // Bulk processing - get selected payrolls
      const selected = payroll.filter((p: any) => selectedPayrolls.includes(p.id))
      if (selected.length === 0) {
        showToastMessage('Please select pending payrolls to process', 'error')
        return
      }
      setProcessingPayrolls(selected)
      setShowProcessModal(true)
    }
  }

  const handleTogglePayrollSelection = (id: string) => {
    setSelectedPayrolls(prev => 
      prev.includes(id) 
        ? prev.filter(pId => pId !== id)
        : [...prev, id]
    )
  }

  const handleSelectAllPayrolls = () => {
    const pendingPayrolls = payroll.filter((p: any) => p.status === 'pending')
    if (selectedPayrolls.length === pendingPayrolls.length) {
      setSelectedPayrolls([])
    } else {
      setSelectedPayrolls(pendingPayrolls.map((p: any) => p.id))
    }
  }

  const handleConfirmProcessPayrolls = async () => {
    if (processingPayrolls.length === 0) {
      showToastMessage('No payrolls selected', 'error')
      return
    }

    try {
      setLoading(true)
      const payrollIds = processingPayrolls.map((p: any) => p.id)
      
      let response
      if (payrollIds.length === 1) {
        response = await apiService.processPayroll(payrollIds[0], {
          status: 'processed',
          payment_method: 'cash'
        })
      } else {
        response = await apiService.processPayrollBulk(payrollIds, {
          status: 'processed',
          payment_method: 'cash'
        })
      }

      if (response.success) {
        showToastMessage(
          (response as any).message || `Successfully processed ${payrollIds.length} payroll(s)`,
          'success'
        )
        setShowProcessModal(false)
        setProcessingPayrolls([])
        setSelectedPayrolls([])
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to process payroll', 'error')
      }
    } catch (error: any) {
      showToastMessage(error.message || 'An error occurred', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleProcessPayroll = () => {
    // Open modal to process payroll for a period
    setModalType('add')
    setEditingItem(null)
    // Set default pay period (current month)
    const now = new Date()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    
    setPayrollForm({
      employee_id: '',
      pay_period_start: firstDay.toISOString().split('T')[0],
      pay_period_end: lastDay.toISOString().split('T')[0]
    })
    setShowModal(true)
  }

  // Loan handlers
  const handleAddLoan = () => {
    setModalType('add')
    setEditingItem(null)
    setShowModal(true)
    setLoanForm({
      employee_id: '',
      loan_type_id: '',
      loan_amount: '',
      loan_date: new Date().toISOString().split('T')[0],
      installment_amount: '',
      total_installments: '',
      description: ''
    })
  }

  const handleSaveLoan = async () => {
    if (!loanForm.employee_id || !loanForm.loan_type_id || !loanForm.loan_amount || !loanForm.loan_date) {
      showToastMessage('Please fill in all required fields', 'error')
      return
    }

    try {
      setLoading(true)
      const data = {
        employee_id: loanForm.employee_id,
        loan_type_id: loanForm.loan_type_id,
        loan_amount: parseFloat(loanForm.loan_amount),
        loan_date: loanForm.loan_date,
        installment_amount: loanForm.installment_amount ? parseFloat(loanForm.installment_amount) : undefined,
        total_installments: loanForm.total_installments ? parseInt(loanForm.total_installments) : undefined,
        description: loanForm.description || undefined
      }

      let response
      if (modalType === 'add') {
        response = await apiService.createLoan(data)
      } else {
        response = await apiService.updateLoan(editingItem.id, data)
      }

      if (response.success) {
        showToastMessage(
          modalType === 'add' ? 'Loan/Advance created successfully' : 'Loan/Advance updated successfully',
          'success'
        )
        setShowModal(false)
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to save loan/advance', 'error')
      }
    } catch (error: any) {
      showToastMessage(error.message || 'An error occurred', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleEditLoan = (loan: Loan) => {
    setModalType('edit')
    setEditingItem(loan)
    setShowModal(true)
    setLoanForm({
      employee_id: loan.employee_id,
      loan_type_id: loan.loan_type_id,
      loan_amount: loan.loan_amount.toString(),
      loan_date: loan.loan_date,
      installment_amount: loan.installment_amount.toString(),
      total_installments: loan.total_installments.toString(),
      description: (loan as any).description || ''
    })
  }

  const handleDeleteLoan = (loan: Loan) => {
    setLoanToDelete(loan)
    setShowDeleteLoanModal(true)
  }

  const confirmDeleteLoan = async () => {
    if (!loanToDelete) return

    try {
      setLoading(true)
      const response = await apiService.deleteLoan(loanToDelete.id)
      if (response.success) {
        showToastMessage('Loan/Advance deleted successfully', 'success')
        loadData()
        setShowDeleteLoanModal(false)
        setLoanToDelete(null)
      } else {
        showToastMessage(response.error || 'Failed to delete loan/advance', 'error')
      }
    } catch (error: any) {
      showToastMessage(error.message || 'An error occurred', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Loan Payment form state
  const [loanPaymentForm, setLoanPaymentForm] = useState({
    loan_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_amount: '',
    installment_number: '',
    payment_method: 'cash',
    notes: ''
  })

  const handleAddLoanPayment = (loan?: Loan) => {
    setModalType('add')
    setEditingItem(null)
    setShowModal(true)
    // Clear loan form to indicate we're recording a payment
    setLoanForm({
      employee_id: '',
      loan_type_id: '',
      loan_amount: '',
      loan_date: new Date().toISOString().split('T')[0],
      installment_amount: '',
      total_installments: '',
      description: ''
    })
    if (loan) {
      setLoanPaymentForm({
        loan_id: loan.id,
        payment_date: new Date().toISOString().split('T')[0],
        payment_amount: loan.total_installments === 1 || loan.total_installments === null ? loan.remaining_balance.toString() : (loan.installment_amount || '').toString(),
        installment_number: ((loan.paid_installments || 0) + 1).toString(),
        payment_method: 'cash',
        notes: ''
      })
    } else {
      setLoanPaymentForm({
        loan_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_amount: '',
        installment_number: '',
        payment_method: 'cash',
        notes: ''
      })
    }
  }

  const handleSaveLoanPayment = async () => {
    if (!loanPaymentForm.loan_id || !loanPaymentForm.payment_date || !loanPaymentForm.payment_amount) {
      showToastMessage('Please fill in all required fields', 'error')
      return
    }

    try {
      setLoading(true)
      const data = {
        loan_id: loanPaymentForm.loan_id,
        payment_date: loanPaymentForm.payment_date,
        payment_amount: parseFloat(loanPaymentForm.payment_amount),
        installment_number: loanPaymentForm.installment_number ? parseInt(loanPaymentForm.installment_number) : undefined,
        payment_method: loanPaymentForm.payment_method || undefined,
        notes: loanPaymentForm.notes || undefined
      }

      const response = await apiService.createLoanPayment(data)

      if (response.success) {
        showToastMessage('Loan payment recorded successfully', 'success')
        setShowModal(false)
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to record loan payment', 'error')
      }
    } catch (error: any) {
      showToastMessage(error.message || 'An error occurred', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleSavePayroll = async () => {
    try {
      if (!payrollForm.pay_period_start || !payrollForm.pay_period_end) {
        showToastMessage('Please fill in pay period dates', 'error')
        return
      }

      // Process payroll for selected employee or all employees
      if (payrollForm.employee_id) {
        // Process for specific employee
        const response = await apiService.createPayroll({
          employee_id: payrollForm.employee_id,
          pay_period_start: payrollForm.pay_period_start,
          pay_period_end: payrollForm.pay_period_end
        })
        
        if (response.success) {
          showToastMessage('Payroll processed successfully', 'success')
          setShowModal(false)
          loadData()
        } else {
          showToastMessage(response.error || 'Failed to process payroll', 'error')
        }
      } else {
        // Process for all active employees
        const activeEmployees = employees.filter(emp => emp.status === 'active')
        let successCount = 0
        let errorCount = 0

        for (const employee of activeEmployees) {
          try {
            const response = await apiService.createPayroll({
              employee_id: employee.id,
              pay_period_start: payrollForm.pay_period_start,
              pay_period_end: payrollForm.pay_period_end
            })
            if (response.success) {
              successCount++
            } else {
              errorCount++
            }
          } catch (error) {
            errorCount++
          }
        }

        if (errorCount === 0) {
          showToastMessage(`Payroll processed successfully for ${successCount} employees`, 'success')
        } else {
          showToastMessage(`Payroll processed for ${successCount} employees, ${errorCount} failed`, 'error')
        }
        setShowModal(false)
        loadData()
      }
    } catch (error) {
      console.error('Error processing payroll:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const handleCancelModal = () => {
    setShowModal(false)
    setEditingItem(null)
  }

  const renderModal = () => {
    if (!showModal) return null

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={handleCancelModal}></div>
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {activeTab === 'payroll' ? 'Process Payroll' :
                 modalType === 'add' ? 'Add' : 'Edit'} {
                  activeTab === 'employees' ? 'Employee' :
                  activeTab === 'attendance' ? 'Attendance Record' :
                  activeTab === 'leaves' ? 'Leave Request' : ''
                }
              </h3>
              <button onClick={handleCancelModal} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {activeTab === 'employees' && (
              <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Employee Code *
                </label>
                <input
                  type="text"
                  value={employeeForm.employee_code}
                  onChange={(e) => setEmployeeForm({...employeeForm, employee_code: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={employeeForm.first_name}
                  onChange={(e) => setEmployeeForm({...employeeForm, first_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={employeeForm.last_name}
                  onChange={(e) => setEmployeeForm({...employeeForm, last_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={employeeForm.email}
                  onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  value={employeeForm.phone}
                  onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Designation
                </label>
                <input
                  type="text"
                  value={employeeForm.designation}
                  onChange={(e) => setEmployeeForm({...employeeForm, designation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department
                </label>
                <input
                  type="text"
                  value={employeeForm.department}
                  onChange={(e) => setEmployeeForm({...employeeForm, department: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              
              {/* Salary Details Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Salary Details</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Base Salary *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={employeeForm.base_salary}
                    onChange={(e) => setEmployeeForm({...employeeForm, base_salary: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    placeholder="0.00"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Allowance
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={employeeForm.allowance}
                    onChange={(e) => setEmployeeForm({...employeeForm, allowance: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    placeholder="0.00"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bonus
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={employeeForm.bonus}
                    onChange={(e) => setEmployeeForm({...employeeForm, bonus: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    placeholder="0.00"
                  />
                </div>
                {(employeeForm.base_salary || employeeForm.allowance || employeeForm.bonus) && (
                  <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Total: {formatCurrency(
                        (parseFloat(employeeForm.base_salary) || 0) +
                        (parseFloat(employeeForm.allowance) || 0) +
                        (parseFloat(employeeForm.bonus) || 0)
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Working Schedule Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Working Schedule</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Working Days Per Week
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="7"
                    value={employeeForm.working_days_per_week}
                    onChange={(e) => setEmployeeForm({...employeeForm, working_days_per_week: parseInt(e.target.value) || 6})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Weekend Days (Select multiple)
                  </label>
                  <select
                    multiple
                    value={Array.isArray(employeeForm.weekend_days) ? employeeForm.weekend_days : []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value)
                      setEmployeeForm({...employeeForm, weekend_days: selected.length > 0 ? selected : ['Friday', 'Saturday']})
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    size={7}
                  >
                    <option value="Sunday">Sunday</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Hold Ctrl/Cmd to select multiple days</p>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Daily Working Hours
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="24"
                    value={employeeForm.daily_working_hours}
                    onChange={(e) => setEmployeeForm({...employeeForm, daily_working_hours: parseFloat(e.target.value) || 8})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    placeholder="8.0"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={employeeForm.status}
                  onChange={(e) => setEmployeeForm({...employeeForm, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </>
            )}

              {activeTab === 'attendance' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Employee *
                  </label>
                  <select
                    value={attendanceForm.employee_id}
                    onChange={(e) => setAttendanceForm({...attendanceForm, employee_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={attendanceForm.attendance_date}
                    onChange={(e) => setAttendanceForm({...attendanceForm, attendance_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Check In *
                  </label>
                  <input
                    type="time"
                    value={attendanceForm.check_in}
                    onChange={(e) => setAttendanceForm({...attendanceForm, check_in: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Check Out
                  </label>
                  <input
                    type="time"
                    value={attendanceForm.check_out}
                    onChange={(e) => setAttendanceForm({...attendanceForm, check_out: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={attendanceForm.status}
                    onChange={(e) => setAttendanceForm({...attendanceForm, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                  </select>
                </div>
              </>
              )}

              {activeTab === 'leaves' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Employee *
                  </label>
                  <select
                    value={leaveForm.employee_id}
                    onChange={(e) => setLeaveForm({...leaveForm, employee_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={leaveForm.start_date}
                    onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={leaveForm.end_date}
                    onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Leave Type *
                  </label>
                  <select
                    value={leaveForm.leave_type_id}
                    onChange={(e) => setLeaveForm({...leaveForm, leave_type_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value="">Select Leave Type</option>
                    {leaveTypes.map(leaveType => (
                      <option key={leaveType.id} value={leaveType.id}>
                        {leaveType.leave_type_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reason
                  </label>
                  <textarea
                    value={leaveForm.reason}
                    onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    rows={3}
                  />
                </div>
              </>
              )}

              {activeTab === 'payroll' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Employee (Optional - Leave blank to process for all active employees)
                    </label>
                    <select
                      value={payrollForm.employee_id}
                      onChange={(e) => setPayrollForm({...payrollForm, employee_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="">All Active Employees</option>
                      {employees.filter(emp => emp.status === 'active').map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pay Period Start *
                    </label>
                    <input
                      type="date"
                      value={payrollForm.pay_period_start}
                      onChange={(e) => setPayrollForm({...payrollForm, pay_period_start: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pay Period End *
                    </label>
                    <input
                      type="date"
                      value={payrollForm.pay_period_end}
                      onChange={(e) => setPayrollForm({...payrollForm, pay_period_end: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                </>
              )}

              {activeTab === 'loans' && modalType === 'add' && editingItem === null && loanPaymentForm.loan_id ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Loan/Advance *
                    </label>
                    <select
                      value={loanPaymentForm.loan_id}
                      onChange={(e) => {
                        const selectedLoan = loans.find(l => l.id === e.target.value)
                        setLoanPaymentForm({
                          ...loanPaymentForm,
                          loan_id: e.target.value,
                          payment_amount: selectedLoan ? (selectedLoan.total_installments === 1 ? selectedLoan.remaining_balance.toString() : (selectedLoan.installment_amount || '').toString()) : '',
                          installment_number: selectedLoan ? ((selectedLoan.paid_installments || 0) + 1).toString() : ''
                        })
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="">Select Loan/Advance</option>
                      {loans.filter(l => l.status === 'active' && l.remaining_balance > 0).map(loan => {
                        const employee = loan.employees
                        const employeeName = employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() : 'Unknown'
                        const loanTypeName = loan.loan_types?.loan_type_name || 'Unknown'
                        return (
                          <option key={loan.id} value={loan.id}>
                            {loan.loan_number} - {employeeName} - {loanTypeName} (Balance: {formatCurrency(loan.remaining_balance)})
                          </option>
                        )
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Payment Date *
                    </label>
                    <input
                      type="date"
                      value={loanPaymentForm.payment_date}
                      onChange={(e) => setLoanPaymentForm({...loanPaymentForm, payment_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Payment Amount *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={loanPaymentForm.payment_amount}
                      onChange={(e) => setLoanPaymentForm({...loanPaymentForm, payment_amount: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Installment Number (Optional)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={loanPaymentForm.installment_number}
                      onChange={(e) => setLoanPaymentForm({...loanPaymentForm, installment_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      placeholder="Auto-calculated"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Payment Method
                    </label>
                    <select
                      value={loanPaymentForm.payment_method}
                      onChange={(e) => setLoanPaymentForm({...loanPaymentForm, payment_method: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="digital_wallet">Digital Wallet</option>
                      <option value="salary_deduction">Salary Deduction</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={loanPaymentForm.notes}
                      onChange={(e) => setLoanPaymentForm({...loanPaymentForm, notes: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      placeholder="Optional notes about this payment"
                    />
                  </div>
                </>
              ) : activeTab === 'loans' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Employee *
                    </label>
                    <select
                      value={loanForm.employee_id}
                      onChange={(e) => setLoanForm({...loanForm, employee_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="">Select Employee</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Loan/Advance Type *
                    </label>
                    <select
                      value={loanForm.loan_type_id}
                      onChange={(e) => setLoanForm({...loanForm, loan_type_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="">Select Type</option>
                      {loanTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.loan_type_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Amount *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={loanForm.loan_amount}
                      onChange={(e) => setLoanForm({...loanForm, loan_amount: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={loanForm.loan_date}
                      onChange={(e) => setLoanForm({...loanForm, loan_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Total Installments (Optional - defaults to loan type max)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={loanForm.total_installments}
                      onChange={(e) => setLoanForm({...loanForm, total_installments: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      placeholder="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Installment Amount (Optional - auto-calculated if not provided)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={loanForm.installment_amount}
                      onChange={(e) => setLoanForm({...loanForm, installment_amount: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      placeholder="Auto-calculated"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={loanForm.description}
                      onChange={(e) => setLoanForm({...loanForm, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      placeholder="Optional notes about this loan/advance"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 gap-3">
              <button
                onClick={handleCancelModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (activeTab === 'employees') handleSaveEmployee()
                  else if (activeTab === 'attendance') handleSaveAttendance()
                  else if (activeTab === 'leaves') handleSaveLeaveRequest()
                  else if (activeTab === 'payroll') handleSavePayroll()
                  else if (activeTab === 'loans') {
                    if (modalType === 'add' && editingItem === null && loanPaymentForm.loan_id) {
                      handleSaveLoanPayment()
                    } else {
                      handleSaveLoan()
                    }
                  }
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                {activeTab === 'payroll' ? 'Process' : 
                 activeTab === 'loans' && modalType === 'add' && editingItem === null && loanPaymentForm.loan_id ? 'Record Payment' :
                 activeTab === 'loans' ? 'Save Loan/Advance' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg ${
          toastType === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toastMessage}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            HR Management
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage employees, attendance, leaves, and payroll
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('employees')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'employees'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <UserGroupIcon className="inline-block h-5 w-5 mr-2" />
            Employees
          </button>
          <button
            onClick={() => setActiveTab('attendance')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'attendance'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <ClockIcon className="inline-block h-5 w-5 mr-2" />
            Attendance
          </button>
          <button
            onClick={() => setActiveTab('leaves')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'leaves'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <CalendarIcon className="inline-block h-5 w-5 mr-2" />
            Leave Requests
          </button>
          <button
            onClick={() => setActiveTab('payroll')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payroll'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <CurrencyDollarIcon className="inline-block h-5 w-5 mr-2" />
            Payroll
          </button>
          <button
            onClick={() => setActiveTab('loans')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'loans'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <CurrencyDollarIcon className="inline-block h-5 w-5 mr-2" />
            Loans & Advances
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'employees' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Employees</h2>
                  <button 
                    onClick={handleAddEmployee}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <UserGroupIcon className="h-5 w-5" />
                    Add Employee
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salary</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {employees.map((employee) => (
                        <tr key={employee.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {employee.employee_code}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {`${employee.first_name} ${employee.last_name}`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {employee.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {employee.designation}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {employee.department}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {formatCurrency((employee as any).base_salary || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              employee.status === 'active' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {employee.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              <button 
                                onClick={() => handleEditEmployee(employee)}
                                className="text-primary-600 hover:text-primary-900 dark:text-primary-400"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button 
                                onClick={() => handleDeleteEmployee(employee)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'attendance' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Attendance</h2>
                  <button 
                    onClick={handleAddAttendance}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <ClockIcon className="h-5 w-5" />
                    Add Record
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check In</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check Out</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {attendance.map((record) => {
                        const employee = (record as any).employees || (record as any).employee
                        const employeeName = employee 
                          ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
                          : 'Unknown'
                        return (
                          <tr key={record.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(record.attendance_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {employeeName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {record.check_in || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {record.check_out || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {record.total_hours || 0}h
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                record.status === 'present' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {record.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button 
                                onClick={() => handleDeleteAttendance(record)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'leaves' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Leave Requests</h2>
                  <button 
                    onClick={handleAddLeaveRequest}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <CalendarIcon className="h-5 w-5" />
                    Add Request
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leave Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {leaveRequests.map((request) => {
                        const employee = (request as any).employees || (request as any).employee
                        const leaveType = (request as any).leave_types || (request as any).leave_type
                        const employeeName = employee 
                          ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
                          : 'Unknown'
                        const leaveTypeName = leaveType?.leave_type_name || 'Unknown'
                        const totalDays = (request as any).total_days || Math.ceil((new Date(request.end_date).getTime() - new Date(request.start_date).getTime()) / (1000 * 60 * 60 * 24))
                        return (
                          <tr key={request.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {employeeName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {leaveTypeName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(request.start_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(request.end_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {totalDays} days
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                request.status === 'approved' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                  : request.status === 'rejected'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              }`}>
                                {request.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                {request.status === 'pending' && (
                                  <button 
                                    onClick={() => handleApproveLeaveRequest(request)}
                                    className="text-green-600 hover:text-green-900 dark:text-green-400"
                                    title="Approve"
                                  >
                                    <CheckCircleIcon className="h-5 w-5" />
                                  </button>
                                )}
                                <button 
                                  onClick={() => handleViewLeaveRequest(request)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400"
                                >
                                  View
                                </button>
                                <button 
                                  onClick={() => handleDeleteLeaveRequest(request)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'payroll' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Payroll</h2>
                  <div className="flex gap-2">
                    {selectedPayrolls.length > 0 && (
                      <button
                        onClick={() => handleProcessPayrollRecord()}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Process Selected ({selectedPayrolls.length})
                      </button>
                    )}
                    <button
                      onClick={() => {
                        const pendingPayrolls = payroll.filter((p: any) => p.status === 'pending')
                        if (pendingPayrolls.length === 0) {
                          showToastMessage('No pending payrolls to process', 'error')
                          return
                        }
                        handleProcessPayrollRecord(pendingPayrolls)
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Process All Pending
                    </button>
                    <button 
                      onClick={async () => {
                        try {
                          setLoading(true)
                          const response = await apiService.recalculateAllPayroll()
                          if (response.success) {
                            showToastMessage(
                              `Recalculated ${(response as any).updated || 0} payroll records`,
                              'success'
                            )
                            loadData()
                          } else {
                            showToastMessage(response.error || 'Failed to recalculate payroll', 'error')
                          }
                        } catch (error) {
                          showToastMessage('An error occurred', 'error')
                        } finally {
                          setLoading(false)
                        }
                      }}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
                    >
                      <ClockIcon className="h-5 w-5" />
                      Recalculate All
                    </button>
                    <button 
                      onClick={handleProcessPayroll}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                    >
                      <CurrencyDollarIcon className="h-5 w-5" />
                      Process Payroll
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          <input
                            type="checkbox"
                            checked={selectedPayrolls.length > 0 && selectedPayrolls.length === payroll.filter((p: any) => p.status === 'pending').length && payroll.filter((p: any) => p.status === 'pending').length > 0}
                            onChange={handleSelectAllPayrolls}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Working Days</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Present Days</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Basic Salary</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allowances</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deductions</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net Salary</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {payroll.map((record) => {
                        const employee = (record as any).employees || (record as any).employee
                        const employeeName = employee 
                          ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
                          : 'Unknown'
                        const isPending = record.status === 'pending'
                        const isSelected = selectedPayrolls.includes(record.id)
                        const attendanceRate = Number((record as any).attendance_rate) || 100
                        return (
                          <tr key={record.id} className={isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {isPending && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleTogglePayrollSelection(record.id)}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {employeeName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {record.pay_period_start && record.pay_period_end
                                ? `${new Date(record.pay_period_start).toLocaleDateString()} - ${new Date(record.pay_period_end).toLocaleDateString()}`
                                : `Month: ${(record as any).payroll_month || 'N/A'}, Year: ${(record as any).payroll_year || 'N/A'}`
                              }
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {(record as any).total_working_days ?? 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {(record as any).days_present ?? 0} / {(record as any).total_working_days ?? 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <span className={`px-2 py-1 rounded ${
                                attendanceRate >= 95 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : attendanceRate >= 80
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {attendanceRate.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency((record as any).base_salary || record.basic_salary || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency((record as any).other_allowances || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(record.deductions || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {formatCurrency(record.net_salary || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                record.status === 'paid' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                  : record.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                              }`}>
                                {record.status || 'pending'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                {record.status === 'pending' && (
                                  <button
                                    onClick={() => handleProcessPayrollRecord(record)}
                                    className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                    title="Process Payroll"
                                  >
                                    Process
                                  </button>
                                )}
                                <button 
                                  onClick={() => handleViewPayroll(record)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400"
                                >
                                  View
                                </button>
                                <button 
                                  onClick={() => handleDeletePayroll(record)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'loans' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Loans & Advances</h2>
                  <button 
                    onClick={handleAddLoan}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Add Loan/Advance
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan Number</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Installments</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remaining</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {loans.map((loan) => {
                        const employee = loan.employees
                        const employeeName = employee 
                          ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
                          : 'Unknown'
                        const loanTypeName = loan.loan_types?.loan_type_name || 'Unknown'
                        
                        return (
                          <tr key={loan.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {loan.loan_number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {employeeName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {loanTypeName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(loan.loan_amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(loan.loan_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {loan.paid_installments}/{loan.total_installments}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(loan.remaining_balance)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                loan.status === 'active' 
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                  : loan.status === 'completed'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {loan.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleAddLoanPayment(loan)}
                                  className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                  title="Record Payment"
                                >
                                  Record Payment
                                </button>
                                <button
                                  onClick={() => handleEditLoan(loan)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteLoan(loan)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                      {loans.length === 0 && (
                        <tr>
                          <td colSpan={9} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                            No loans or advances found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {renderModal()}

      {/* Process Payroll Modal */}
      {showProcessModal && processingPayrolls.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Process Payroll{processingPayrolls.length > 1 ? 's' : ''}
                </h2>
                <button
                  onClick={() => {
                    setShowProcessModal(false)
                    setProcessingPayrolls([])
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You are about to process {processingPayrolls.length} payroll{processingPayrolls.length > 1 ? 's' : ''}. This will:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Change status from 'pending' to 'processed'</li>
                <li>Record loan and advance repayments</li>
                <li>Update loan balances automatically</li>
                <li>Create journal entries in the accounting module</li>
              </ul>

              <div className="max-h-60 overflow-y-auto mb-6">
                <div className="space-y-2">
                  {processingPayrolls.map((p: any) => {
                    const emp = p.employees || p.employee
                    return (
                      <div key={p.id} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {emp ? `${emp.first_name || ''} ${emp.last_name || ''}`.trim() : 'N/A'}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {p.payroll_month || 'N/A'}/{p.payroll_year || 'N/A'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Net Salary: {formatCurrency(p.net_salary || 0)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowProcessModal(false)
                    setProcessingPayrolls([])
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmProcessPayrolls}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    `Process ${processingPayrolls.length} Payroll${processingPayrolls.length > 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payroll View Modal */}
      {showViewModal && viewingPayroll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Payroll Details
                </h2>
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setViewingPayroll(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Employee Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Employee Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Employee Name
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {viewingPayroll.employees 
                        ? `${viewingPayroll.employees.first_name || ''} ${viewingPayroll.employees.last_name || ''}`.trim()
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pay Period
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {viewingPayroll.pay_period_start && viewingPayroll.pay_period_end
                        ? `${new Date(viewingPayroll.pay_period_start).toLocaleDateString()} - ${new Date(viewingPayroll.pay_period_end).toLocaleDateString()}`
                        : `Month: ${viewingPayroll.payroll_month || 'N/A'}, Year: ${viewingPayroll.payroll_year || 'N/A'}`}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      viewingPayroll.status === 'paid' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : viewingPayroll.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    }`}>
                      {viewingPayroll.status || 'pending'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Attendance Summary */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Attendance Summary
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Working Days
                    </label>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {viewingPayroll.total_working_days || 0}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Present Days
                    </label>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {viewingPayroll.days_present || 0}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Absent Days
                    </label>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {viewingPayroll.days_absent || 0}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Attendance Rate
                    </label>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {(viewingPayroll.attendance_rate || 0).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Earnings */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Earnings
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Basic Salary</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(viewingPayroll.base_salary || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Allowances & Bonus</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(viewingPayroll.other_allowances || 0)}
                    </span>
                  </div>
                  <div className="border-t border-gray-300 dark:border-gray-600 pt-3 flex justify-between">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">Gross Salary</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(viewingPayroll.gross_salary || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Deductions
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Absences Deduction</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(viewingPayroll.absences_deduction || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Late Deduction</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(viewingPayroll.late_deduction || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Loan Deduction</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(viewingPayroll.loan_deduction || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Advance Deduction</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(viewingPayroll.advance_deduction || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Other Deductions</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(viewingPayroll.other_deductions || 0)}
                    </span>
                  </div>
                  <div className="border-t border-gray-300 dark:border-gray-600 pt-3 flex justify-between">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">Total Deductions</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(viewingPayroll.total_deductions || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Net Salary */}
              <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">Net Salary</span>
                  <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {formatCurrency(viewingPayroll.net_salary || 0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setViewingPayroll(null)
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Employee Confirmation Modal */}
      {showDeleteEmployeeModal && employeeToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Employee
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Are you sure you want to delete this employee?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="font-medium text-gray-900 dark:text-white">
                  {employeeToDelete.first_name} {employeeToDelete.last_name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Code: {employeeToDelete.employee_code}
                </p>
                {employeeToDelete.email && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Email: {employeeToDelete.email}
                  </p>
                )}
                {employeeToDelete.department && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Department: {employeeToDelete.department}
                  </p>
                )}
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                This will also delete all related records (attendance, leave requests, payroll, loans). This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteEmployeeModal(false)
                  setEmployeeToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteEmployee}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete Employee
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Attendance Confirmation Modal */}
      {showDeleteAttendanceModal && attendanceToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Attendance Record
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Are you sure you want to delete this attendance record?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Date: {new Date(attendanceToDelete.attendance_date).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Check In: {attendanceToDelete.check_in || '-'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Check Out: {attendanceToDelete.check_out || '-'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Status: {attendanceToDelete.status}
                </p>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteAttendanceModal(false)
                  setAttendanceToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteAttendance}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete Record
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Leave Request Confirmation Modal */}
      {showDeleteLeaveModal && leaveToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Leave Request
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Are you sure you want to delete this leave request?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Start Date: {new Date(leaveToDelete.start_date).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  End Date: {new Date(leaveToDelete.end_date).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Status: {leaveToDelete.status}
                </p>
                {leaveToDelete.reason && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Reason: {leaveToDelete.reason}
                  </p>
                )}
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteLeaveModal(false)
                  setLeaveToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteLeaveRequest}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Payroll Confirmation Modal */}
      {showDeletePayrollModal && payrollToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Payroll Record
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Are you sure you want to delete this payroll record?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Period: {new Date(payrollToDelete.pay_period_start).toLocaleDateString()} - {new Date(payrollToDelete.pay_period_end).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Net Salary: {formatCurrency(payrollToDelete.net_salary || 0)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Status: {payrollToDelete.status}
                </p>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                {payrollToDelete.status === 'paid' || payrollToDelete.status === 'processed' 
                  ? 'Warning: This payroll has been processed/paid. Deleting it may affect accounting records. This action cannot be undone.'
                  : 'This action cannot be undone.'}
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeletePayrollModal(false)
                  setPayrollToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePayroll}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete Payroll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Loan Confirmation Modal */}
      {showDeleteLoanModal && loanToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Loan/Advance
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Are you sure you want to delete this loan/advance?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="font-medium text-gray-900 dark:text-white">
                  {loanToDelete.loan_number}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Amount: {formatCurrency(loanToDelete.loan_amount || 0)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Date: {new Date(loanToDelete.loan_date).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Remaining Balance: {formatCurrency(loanToDelete.remaining_balance || 0)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Status: {loanToDelete.status}
                </p>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                This will also delete all related payment records. This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteLoanModal(false)
                  setLoanToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteLoan}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete Loan/Advance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve/Reject Leave Request Modal */}
      {showApproveLeaveModal && leaveToApprove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Review Leave Request
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                Please review the leave request details:
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Employee
                  </label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {(() => {
                      const employee = (leaveToApprove as any).employees || (leaveToApprove as any).employee
                      return employee 
                        ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.employee_code || 'N/A'
                        : 'N/A'
                    })()}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Leave Type
                  </label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {(() => {
                      const leaveType = (leaveToApprove as any).leave_types || (leaveToApprove as any).leave_type
                      return leaveType?.leave_type_name || 'N/A'
                    })()}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Start Date
                    </label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(leaveToApprove.start_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      End Date
                    </label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(leaveToApprove.end_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Total Days
                  </label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {(() => {
                      const totalDays = (leaveToApprove as any).total_days || 
                        Math.ceil((new Date(leaveToApprove.end_date).getTime() - new Date(leaveToApprove.start_date).getTime()) / (1000 * 60 * 60 * 24))
                      return `${totalDays} day${totalDays !== 1 ? 's' : ''}`
                    })()}
                  </p>
                </div>
                {leaveToApprove.reason && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Reason
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {leaveToApprove.reason}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Rejection Reason (Optional - only required if rejecting)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowApproveLeaveModal(false)
                  setLeaveToApprove(null)
                  setRejectionReason('')
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRejectLeaveRequest}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Reject
              </button>
              <button
                onClick={confirmApproveLeaveRequest}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HRManagement

